import * as Sentry from '@sentry/react';

const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
const environment = (
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  process.env.NODE_ENV ||
  'development'
).trim();
const enableLocalhost = process.env.REACT_APP_SENTRY_ENABLE_LOCALHOST === 'true';

const isBrowser = () => typeof window !== 'undefined';
const isLocalhost = () =>
  isBrowser() &&
  ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(window.location.hostname);

let initialized = false;

export const isSentryEnabled = () =>
  Boolean(dsn && isBrowser() && (!isLocalhost() || enableLocalhost));

export const initSentry = () => {
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.info('[Sentry] skipped: REACT_APP_SENTRY_DSN not set (restart npm start after editing .env)');
    return false;
  }
  if (isLocalhost() && !enableLocalhost) {
    // eslint-disable-next-line no-console
    console.info('[Sentry] skipped on localhost (set REACT_APP_SENTRY_ENABLE_LOCALHOST=true)');
    return false;
  }
  if (!isSentryEnabled() || initialized) {
    return initialized;
  }

  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
    // Session Replay: sample errors heavily, normal sessions lightly
    replaysSessionSampleRate: Number(
      process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.0
    ),
    replaysOnErrorSampleRate: Number(
      process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1.0
    ),
    dataCollection: {
      // To disable sending user data and HTTP bodies, set:
      // userInfo: false,
      // httpBodies: [],
    },
    ignoreErrors: [
      // Benign browser noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });

  initialized = true;
  // Helpful for local verification (DevTools console throw is unreliable)
  if (typeof window !== 'undefined') {
    window.__FASHDECK_SENTRY__ = Sentry;
    // eslint-disable-next-line no-console
    console.info(`[Sentry] initialized (environment=${environment})`);
  }
  return true;
};

export const captureException = (error, context) => {
  if (!initialized) return;
  if (context) {
    Sentry.withScope((scope) => {
      if (context.tags) {
        Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
      }
      if (context.extra) {
        Object.entries(context.extra).forEach(([k, v]) => scope.setExtra(k, v));
      }
      Sentry.captureException(error);
    });
    return;
  }
  Sentry.captureException(error);
};

export const setSentryUser = (user) => {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id || user._id || undefined,
    email: user.email || undefined,
    username: user.name || user.username || undefined,
  });
};

export { Sentry };
